// select.js
const dataPrefix = 'data-select';

export function select($container, options = {}) {
    // options:
    // name: string
    // label: string
    // items: initial array [{ value, label, selected?, disabled? }]
    // placeholder: string
    // required: boolean
    // searchable: boolean
    // pageSize: number (для ленивой загрузки)
    // loadOptions: async function ({ search, offset, limit }) -> { items: [], hasMore: boolean } | items[]
    // onSelect: function(selectedItem) - callback при выборе
    const {
        name = '',
        label = '',
        items: initialItems = [],
        placeholder = '',
        required = false,
        searchable = false,
        pageSize = 50,
        loadOptions = null,
        onSelect = null,
    } = options;

    renderSelect();

    function renderSelect() {
        const $template = document.querySelector(`[${dataPrefix}-template]`);
        if (!$template || !$template.content) return;

        const fragment = $template.content.cloneNode(true);
        const $formEl = fragment.querySelector(`[${dataPrefix}-form]`);
        if (!$formEl) return;

        // unique id
        const uniqueId = `select-${Date.now()}-${Math.random().toString(36).substr(2,5)}`;

        // nodes
        const $native = $formEl.querySelector(`[${dataPrefix}-native]`);
        const $control = $formEl.querySelector(`[${dataPrefix}-control]`);
        const $value = $formEl.querySelector(`[${dataPrefix}-value]`);
        const $toggle = $formEl.querySelector(`[${dataPrefix}-toggle]`);
        const $labelEl = $formEl.querySelector(`[${dataPrefix}-label]`);
        const $list = $formEl.querySelector(`[${dataPrefix}-list]`);

        if (!$native || !$control || !$value || !$list) return;

        // attributes
        $formEl.setAttribute('id', uniqueId + '-wrap');

        if (name) {
            $native.setAttribute('name', name);
        }
        $native.setAttribute('id', uniqueId);
        $native.style.display = 'none'; // keep native select for form submit / screen readers

        if (required) {
            $native.setAttribute('required', '');
            $native.setAttribute('aria-required', 'true');
        }

        if ($labelEl) {
            $labelEl.setAttribute('for', uniqueId);
            $labelEl.textContent = label;
        }

        // internal state
        let isOpen = false;
        let focusedIndex = -1;
        let items = []; // текущие items в списке (массив объектов)
        let itemsMap = new Map(); // value -> item
        let isLoading = false;
        let hasMore = false;
        let offset = 0;
        let currentSearch = '';

        // create search input if needed
        let $searchInput = null;
        if (searchable) {
            $searchInput = document.createElement('input');
            $searchInput.type = 'search';
            $searchInput.className = 'select__search';
            $searchInput.setAttribute('aria-label', 'Search');
            $searchInput.setAttribute('placeholder', 'Поиск...');
            $searchInput.tabIndex = 0;
            // will be prepended into $list when rendering
        }

        // loading node
        const loadingNode = document.createElement('li');
        loadingNode.className = 'select__list-loading';
        loadingNode.textContent = 'Загрузка...';
        loadingNode.style.display = 'none';
        loadingNode.tabIndex = -1;

        // helper to produce list item element from item object
        function createListItem(it) {
            const li = document.createElement('li');
            li.setAttribute('role', 'option');
            li.className = 'select__list-item';
            li.dataset.value = it.value;
            li.tabIndex = -1;
            if (it.disabled) {
                li.classList.add('disabled');
                li.setAttribute('aria-disabled', 'true');
            }
            li.setAttribute('aria-selected', it.selected ? 'true' : 'false');
            if (it.selected) li.classList.add('selected');
            li.textContent = it.label;

            li.addEventListener('click', (e) => {
                e.stopPropagation();
                if (it.disabled) return;
                selectValue(it.value);
                closeList();
                $toggle.focus();
            });

            li.addEventListener('mouseenter', () => {
                setActiveDescendant(li);
                // set focusedIndex accordingly
                const arr = $items();
                focusedIndex = arr.indexOf(li);
                arr.forEach(n => n.classList.remove('focused'));
                li.classList.add('focused');
            });

            return li;
        }

        // render native and custom list from items array
        function renderOptions(newItems = [], append = false) {
            if (!append) {
                items = [];
                itemsMap.clear();
                offset = 0;
            }

            // merge/append items
            newItems.forEach(it => {
                // normalize structure
                const item = {
                    value: String(it.value),
                    label: String(it.label),
                    disabled: !!it.disabled,
                    selected: !!it.selected,
                };
                // avoid duplicates (by value)
                if (!itemsMap.has(item.value)) {
                    items.push(item);
                    itemsMap.set(item.value, item);
                }
            });

            // update native select and custom list
            if (!append) {
                $native.innerHTML = '';
                // clear list keep search input and loading node
                // remove all list items except search (if present) and loading node
                Array.from($list.children).forEach(ch => $list.removeChild(ch));
                if (searchable && $searchInput) $list.appendChild(wrapSearchInput($searchInput));
            }

            // add options for new items
            newItems.forEach(it => {
                const val = String(it.value);
                // native option
                if (!$native.querySelector(`option[value="${escapeCssSelector(val)}"]`)) {
                    const opt = document.createElement('option');
                    opt.value = val;
                    opt.textContent = it.label;
                    if (it.disabled) opt.disabled = true;
                    if (it.selected) opt.selected = true;
                    $native.appendChild(opt);
                }
                // custom list - append item node
                // check if already exists
                if (!$list.querySelector(`.select__list-item[data-value="${cssAttrEscape(val)}"]`)) {
                    const li = createListItem(it);
                    $list.appendChild(li);
                }
            });

            // append loading node if not present
            if (!$list.querySelector('.select__list-loading')) {
                $list.appendChild(loadingNode);
            }

            // update offset
            offset = items.length;
        }

        function wrapSearchInput(input) {
            // put search input in a wrapper <li> to keep list semantics
            const wrapper = document.createElement('li');
            wrapper.className = 'select__list-search';
            wrapper.setAttribute('role', 'presentation');
            wrapper.appendChild(input);
            return wrapper;
        }

        // initial render: if initialItems provided, render them
        if (initialItems && initialItems.length) {
            renderOptions(initialItems, false);
        } else {
            // if loadOptions is provided, we'll load initial page on open (or immediately if desired)
            // here we choose lazy: load on open
        }

        // set initial display from native select or initialItems
        const selNativeOpt = $native.querySelector('option[selected]') || $native.options[$native.selectedIndex];
        if (selNativeOpt && selNativeOpt.value) {
            $value.textContent = selNativeOpt.textContent;
            $formEl.classList.add('active');
        } else {
            // if any initialItems had .selected true, that was already synced
            const selectedItem = items.find(i => i.selected);
            if (selectedItem) {
                $value.textContent = selectedItem.label;
                $formEl.classList.add('active');
            } else {
                $value.textContent = placeholder || '';
            }
        }

        const $items = () => Array.from($list.querySelectorAll('.select__list-item:not(.disabled)'));

        // helpers: open/close/toggle
        function openList() {
            if (isOpen) return;
            isOpen = true;
            $list.style.display = 'block';
            $control.setAttribute('aria-expanded', 'true');
            $formEl.classList.add('active');
            document.addEventListener('click', outsideClickHandler);
            document.addEventListener('keydown', keydownHandler);

            // if loadOptions provided and items empty -> load initial page
            if (loadOptions && items.length === 0 && !isLoading) {
                // reset state
                offset = 0;
                currentSearch = '';
                hasMore = true;
                loadMore();
            }

            // set focusedIndex to selected or first
            const selected = $list.querySelector('.select__list-item.selected');
            const arr = $items();
            if (selected) {
                focusedIndex = arr.indexOf(selected);
            } else {
                focusedIndex = 0;
            }
            // if search present, focus it (user expects to type)
            if (searchable && $searchInput) {
                setTimeout(() => $searchInput.focus(), 0);
            } else {
                focusItemByIndex(focusedIndex);
            }
        }

        function closeList() {
            if (!isOpen) return;
            isOpen = false;
            $list.style.display = 'none';
            $control.setAttribute('aria-expanded', 'false');
            document.removeEventListener('click', outsideClickHandler);
            document.removeEventListener('keydown', keydownHandler);
            if (!$native.value) {
                $formEl.classList.remove('active');
            }
        }

        function toggleList() {
            if (isOpen) closeList();
            else openList();
        }

        function outsideClickHandler(e) {
            if (!$formEl.contains(e.target)) {
                closeList();
            }
        }

        // keyboard handler
        function keydownHandler(e) {
            const arr = $items();
            if (!arr.length) return;
            // if search input is focused, handle special cases
            const activeEl = document.activeElement;
            const searchFocused = searchable && $searchInput && (activeEl === $searchInput);

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    if (!isOpen) {
                        openList();
                        return;
                    }
                    if (searchFocused) {
                        // move focus to first item
                        focusedIndex = 0;
                        focusItemByIndex(focusedIndex);
                        arr[focusedIndex] && arr[focusedIndex].focus();
                        return;
                    }
                    focusedIndex = Math.min(arr.length - 1, focusedIndex + 1);
                    focusItemByIndex(focusedIndex);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    if (!isOpen) {
                        openList();
                        return;
                    }
                    if (searchFocused) {
                        // do nothing or move to last?
                        focusedIndex = arr.length - 1;
                        focusItemByIndex(focusedIndex);
                        arr[focusedIndex] && arr[focusedIndex].focus();
                        return;
                    }
                    focusedIndex = Math.max(0, focusedIndex - 1);
                    focusItemByIndex(focusedIndex);
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (isOpen && focusedIndex >= 0 && !searchFocused) {
                        const node = arr[focusedIndex];
                        if (node && !node.classList.contains('disabled')) {
                            selectValue(node.dataset.value);
                            closeList();
                        }
                    } else if (!isOpen) {
                        openList();
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    closeList();
                    break;
                case 'Tab':
                    closeList();
                    break;
                default:
                    // nothing
                    break;
            }
        }

        function focusItemByIndex(idx) {
            const arr = $items();
            if (!arr.length) return;
            arr.forEach(n => n.classList.remove('focused'));
            if (idx < 0 || idx >= arr.length) return;
            const node = arr[idx];
            node.classList.add('focused');
            node.scrollIntoView({ block: 'nearest' });
            setActiveDescendant(node);
        }

        function setActiveDescendant(node) {
            if (node) {
                const id = node.id || (node.id = uniqueId + '-opt-' + Math.random().toString(36).substr(2,5));
                $control.setAttribute('aria-activedescendant', id);
            } else {
                $control.removeAttribute('aria-activedescendant');
            }
        }

        function selectValue(val) {
            // find item in itemsMap
            const item = itemsMap.get(String(val));
            // if item not found (maybe lazy returned new data), try to find native option label
            let label = '';
            if (item) label = item.label;
            const opt = $native.querySelector(`option[value="${escapeCssSelector(String(val))}"]`);
            // update list selection markers
            const listItems = Array.from($list.querySelectorAll('.select__list-item'));
            listItems.forEach(li => {
                if (li.dataset.value === String(val)) {
                    li.classList.add('selected');
                    li.setAttribute('aria-selected', 'true');
                } else {
                    li.classList.remove('selected');
                    li.setAttribute('aria-selected', 'false');
                }
            });
            if (opt) {
                opt.selected = true;
                $value.textContent = opt.textContent;
                $formEl.classList.add('active');
            } else {
                // create native option if missing
                const newOpt = document.createElement('option');
                newOpt.value = String(val);
                newOpt.textContent = label || String(val);
                newOpt.selected = true;
                $native.appendChild(newOpt);
                $value.textContent = newOpt.textContent;
                $formEl.classList.add('active');
            }

            // trigger native change event
            const evt = new Event('change', { bubbles: true });
            $native.dispatchEvent(evt);

            // call user callback if provided
            if (typeof onSelect === 'function') {
                // try to provide item object
                const selectedItem = item || { value: String(val), label: $value.textContent };
                try {
                    onSelect(selectedItem);
                } catch (err) {
                    // swallow user errors
                    console.error('onSelect callback error', err);
                }
            }

            // also dispatch a custom event from $formEl for external listeners
            const customEvt = new CustomEvent('select:change', { detail: { value: String(val), item: item || null } });
            $formEl.dispatchEvent(customEvt);
        }

        // utility to find option by CSS-escaped value
        function escapeCssSelector(s) {
            return String(s).replace(/"/g, '\\"');
        }
        // for attribute selector quotes safety
        function cssAttrEscape(s) {
            return String(s).replace(/"/g, '\\"').replace(/'/g, "\\'");
        }

        // ---------- Lazy loading logic ----------
        async function loadMore() {
            if (!loadOptions || isLoading || (hasMore === false && offset > 0)) return;
            isLoading = true;
            loadingNode.style.display = 'block';

            try {
                const resp = await loadOptions({ search: currentSearch, offset, limit: pageSize });
                // normalize
                let newItems = [];
                let localHasMore = false;
                if (Array.isArray(resp)) {
                    newItems = resp;
                    localHasMore = newItems.length >= pageSize;
                } else if (resp && Array.isArray(resp.items)) {
                    newItems = resp.items;
                    localHasMore = !!resp.hasMore;
                } else {
                    console.warn('loadOptions should return array or { items, hasMore }');
                }

                // map to normalized objects
                const normalized = newItems.map(it => ({
                    value: it.value,
                    label: it.label,
                    disabled: !!it.disabled,
                    selected: !!it.selected,
                }));

                // render append
                renderOptions(normalized, true);

                // update flags
                hasMore = localHasMore;
                offset = items.length;
            } catch (err) {
                console.error('Error in loadOptions:', err);
            } finally {
                isLoading = false;
                loadingNode.style.display = 'none';
            }
        }

        // scroll handler for infinite loading
        function onListScroll(e) {
            if (!hasMore || isLoading) return;
            const threshold = 150; // px from bottom to trigger
            const el = e.target;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
                loadMore();
            }
        }

        // ---------- Search handling (debounced) ----------
        function debounce(fn, wait = 300) {
            let t = null;
            return (...args) => {
                if (t) clearTimeout(t);
                t = setTimeout(() => fn.apply(null, args), wait);
            };
        }

        const onSearchInput = debounce(async (ev) => {
            const q = ev.target.value.trim();
            currentSearch = q;
            // reset listing
            Array.from($list.querySelectorAll('.select__list-item')).forEach(n => n.remove());
            items = [];
            itemsMap.clear();
            offset = 0;
            hasMore = true;
            // reset native options
            $native.innerHTML = '';
            // if initial static items were provided AND no loadOptions - we filter client-side
            if (!loadOptions) {
                const filtered = initialItems.filter(it => it.label.toLowerCase().includes(q.toLowerCase()));
                renderOptions(filtered, false);
            } else {
                // load first page for search
                await loadMore();
            }
        }, 300);

        // wrap up event listeners and UI
        // open/toggle events
        $toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            toggleList();
        });

        $control.addEventListener('click', (e) => {
            e.stopPropagation();
            toggleList();
        });

        // keyboard open on container focus (to mimic input)
        $formEl.addEventListener('focus', () => {
            $formEl.classList.add('focus');
        });
        $formEl.addEventListener('blur', () => {
            $formEl.classList.remove('focus');
        });

        // keep native select value synced if changed programmatically
        $native.addEventListener('change', () => {
            const sel = $native.options[$native.selectedIndex];
            if (sel) {
                $value.textContent = sel.textContent;
                const listItems = Array.from($list.querySelectorAll('.select__list-item'));
                listItems.forEach(li => {
                    if (li.dataset.value === sel.value) {
                        li.classList.add('selected');
                        li.setAttribute('aria-selected', 'true');
                    } else {
                        li.classList.remove('selected');
                        li.setAttribute('aria-selected', 'false');
                    }
                });
                if (sel.value) {
                    $formEl.classList.add('active');
                } else {
                    if (!placeholder) $formEl.classList.remove('active');
                }
            }
        });

        // search input listeners
        if (searchable && $searchInput) {
            // insert search input as first li
            const wrapper = wrapSearchInput($searchInput);
            $list.insertBefore(wrapper, $list.firstChild);

            $searchInput.addEventListener('input', onSearchInput);

            // when pressing arrow down from search - move focus to first list item
            $searchInput.addEventListener('keydown', (e) => {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    const arr = $items();
                    if (arr.length) {
                        focusedIndex = 0;
                        focusItemByIndex(0);
                        arr[0].focus();
                    }
                }
            });
        }

        // scroll => lazy load
        $list.addEventListener('scroll', onListScroll);

        // append loading node if not exists
        if (!$list.querySelector('.select__list-loading')) {
            $list.appendChild(loadingNode);
        }

        // finally append to container
        $container.appendChild(fragment);
    }
}
